/**
 * ui.tsx — the presentational primitives shared by every screen, ported from the mockup's markup
 * so the class names of `styles/theme.css` apply unchanged.
 *
 * Rules enforced here (spec-business §8, mockup-rules.md):
 *  - money direction is never colour alone: `<Money>` prints the sign, `<Delta>` adds ↑/↓;
 *  - every number, date, mask and id is LTR-isolated (`.num` / `.ltr` / `.mask`);
 *  - status is chip + icon + text.
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { fmtMoney, fmtPct } from '../format.ts';
import { he } from '../i18n/he.ts';
import { Link } from '../hooks/useRouter.tsx';
import { EmptyState as KitEmptyState } from '../../../../shared/ui/src/index.tsx';
import type { IllustrationName } from '../../../../shared/ui/src/index.tsx';

/** An LTR-isolated, tabular span for numbers and dates. */
export function Num({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={`num${className ? ` ${className}` : ''}`}>{children}</span>;
}

/** LTR-isolated text: emails, URLs, tokens. */
export function Ltr({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={`ltr${className ? ` ${className}` : ''}`}>{children}</span>;
}

/** Account numbers, card masks and invoice ids (spec §8 "מספרים"). */
export function Mask({ children }: { children: ReactNode }) {
  return <span className="mask">{children}</span>;
}

/** Money with the spec's formatting; colour is never the only cue — the sign always shows. */
export function Money({
  agorot,
  decimals = 0,
  plus,
  className,
}: {
  agorot: number | null | undefined;
  decimals?: number;
  plus?: boolean;
  className?: string;
}) {
  return <span className={`num${className ? ` ${className}` : ''}`}>{fmtMoney(agorot, decimals, plus)}</span>;
}

/**
 * A signed percentage with an arrow (spec §4.1: "|Δ| < 2% → אפור־ניטרלי; ≥ 2% → ירוק/אדום").
 * `upGood = false` flips the semantics for expenses, where a rise is bad.
 */
export function Delta({ pct, upGood = true, label }: { pct: number | null | undefined; upGood?: boolean; label?: string }) {
  if (pct == null || !Number.isFinite(pct)) return null;
  const flat = Math.abs(pct) < 2;
  const good = pct > 0 ? upGood : !upGood;
  const tone = flat ? 'flat' : good ? 'good' : 'bad';
  const icon = flat ? 'minus' : pct > 0 ? 'arrowUp' : 'arrowDown';
  return (
    <>
      <span className={`delta ${tone}`}>
        <Icon name={icon} />
        <Num>{fmtPct(pct)}</Num>
      </span>
      {label ? <span className="sn">{label}</span> : null}
    </>
  );
}

/** A money delta with the same rule, used by the hero and the forecast tiles. */
export function DeltaMoney({ agorot, baseAgorot, upGood = true }: { agorot: number; baseAgorot: number; upGood?: boolean }) {
  const pct = baseAgorot ? (agorot / baseAgorot) * 100 : 0;
  const flat = Math.abs(pct) < 2;
  const good = agorot > 0 ? upGood : !upGood;
  const tone = flat ? 'flat' : good ? 'good' : 'bad';
  const icon = flat ? 'minus' : agorot > 0 ? 'arrowUp' : 'arrowDown';
  return (
    <span className={`delta ${tone}`}>
      <Icon name={icon} />
      <Money agorot={agorot} plus />
    </span>
  );
}

export function Chip({
  children,
  tone,
  icon,
  small,
  title,
}: {
  children: ReactNode;
  tone?: 'good' | 'warn' | 'critical' | 'info' | 'accent' | 'outline' | 'amber';
  icon?: string;
  small?: boolean;
  title?: string;
}) {
  return (
    <span className={`chip${tone ? ` ${tone}` : ''}${small ? ' sm' : ''}`} title={title}>
      {icon ? <Icon name={icon} /> : null}
      <span>{children}</span>
    </span>
  );
}

/** The "יתחבר ל-LabTop" dashed tag. */
export function DashedTag({ children, icon = 'plug' }: { children: ReactNode; icon?: string }) {
  return (
    <span className="tag-lt">
      <Icon name={icon} />
      {children}
    </span>
  );
}

export function Card({
  children,
  span,
  hero,
  className,
  labelledBy,
  ariaLabel,
}: {
  children: ReactNode;
  span?: number;
  hero?: boolean;
  className?: string;
  labelledBy?: string;
  ariaLabel?: string;
}) {
  return (
    <section
      className={`card${hero ? ' hero' : ''}${span ? ` col-${span}` : ''}${className ? ` ${className}` : ''}`}
      aria-labelledby={labelledBy}
      aria-label={ariaLabel}
    >
      {children}
    </section>
  );
}

export function CardHead({ title, titleId, sub, actions }: { title: ReactNode; titleId?: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="card-h">
      <div className="ct">
        <h2 className="card-t" id={titleId}>
          {title}
        </h2>
        {sub ? <div className="card-s">{sub}</div> : null}
      </div>
      {actions ? <div className="card-a">{actions}</div> : null}
    </div>
  );
}

/** The "← לתחזית המלאה" card footer link (the chevron is mirrored for RTL by the icon set). */
export function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <div className="card-f">
      <Link className="link-btn" to={to}>
        {children} <Icon name="arrowLeft" />
      </Link>
    </div>
  );
}

export function Row({
  label,
  value,
  total,
  muted,
  to,
  onClick,
}: {
  label: ReactNode;
  value?: ReactNode;
  total?: boolean;
  muted?: boolean;
  to?: string;
  onClick?: () => void;
}) {
  const className = `row${total ? ' total' : ''}${muted ? ' muted' : ''}${to || onClick ? ' link' : ''}`;
  const inner = (
    <>
      <span className="lbl">{label}</span>
      {value !== undefined ? <span className="val">{value}</span> : null}
    </>
  );
  if (to)
    return (
      <Link className={className} to={to}>
        {inner}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" className={className} onClick={onClick}>
        {inner}
      </button>
    );
  return <div className={className}>{inner}</div>;
}

/**
 * A figure tile. `hero` raises its value to the 36px+ hero size of REDESIGN §8 — **at most one
 * tile per screen may carry it**, because "One hero per screen. Everything else steps down."
 */
export function Tile({
  label,
  value,
  note,
  tone,
  to,
  onClick,
  children,
  tight,
  hero,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'critical' | 'warn';
  to?: string;
  onClick?: () => void;
  children?: ReactNode;
  tight?: boolean;
  hero?: boolean;
}) {
  const className = `tile${tight ? ' tight' : ''}${hero ? ' tile--hero' : ''}${tone ? ` ${tone}` : ''}${to || onClick ? ' link' : ''}`;
  const inner = (
    <>
      <div className="tl">{label}</div>
      <div className="tv">{value}</div>
      {note ? <div className="tn">{note}</div> : null}
      {children}
    </>
  );
  if (to)
    return (
      <Link className={className} to={to}>
        {inner}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" className={className} onClick={onClick}>
        {inner}
      </button>
    );
  return <div className={className}>{inner}</div>;
}

export function Stat({ label, value, note, to }: { label: ReactNode; value: ReactNode; note?: ReactNode; to?: string }) {
  const inner = (
    <>
      <div className="sl">{label}</div>
      <div className="sv">{value}</div>
      {note ? <div className="sn">{note}</div> : null}
    </>
  );
  return to ? (
    <Link className="stat link" to={to}>
      {inner}
    </Link>
  ) : (
    <div className="stat">{inner}</div>
  );
}

/**
 * REDESIGN §7d: "Never a bare אין נתונים." Every empty list in the app already called this
 * primitive, so routing it through the kit's `EmptyState` gives all of them a line illustration,
 * a heading and one sentence at once — no call site has to change.
 *
 * `icon` (the old API) picks the illustration; `illustration` overrides it. With only `text` the
 * sentence IS the heading, which keeps every existing message exactly as written; pass `title` as
 * well when the state deserves a heading plus an explanation.
 */
const EMPTY_ART: Readonly<Record<string, IllustrationName>> = {
  checkCircle: 'noAnomalies',
  check: 'noAnomalies',
  bell: 'noAlerts',
  alertCircle: 'noAlerts',
  alertTri: 'noAlerts',
  inbox: 'noInvoices',
  file: 'noInvoices',
  receipt: 'noInvoices',
  landmark: 'noInvoices',
  filter: 'noResults',
  search: 'noResults',
  list: 'noResults',
  plug: 'reconnect',
  link: 'reconnect',
  refresh: 'reconnect',
  server: 'firstRun',
  wrench: 'firstRun',
};

export function EmptyState({
  icon = 'inbox',
  illustration,
  title,
  text,
  action,
  good,
  variant = 'inline',
}: {
  icon?: string;
  illustration?: IllustrationName;
  title?: string;
  text: string;
  action?: ReactNode;
  good?: boolean;
  variant?: 'page' | 'inline';
}) {
  const art: IllustrationName = illustration ?? (good ? 'noAnomalies' : (EMPTY_ART[icon] ?? 'noResults'));
  return <KitEmptyState illustration={art} title={title ?? text} body={title ? text : undefined} action={action} variant={variant} />;
}

export function ErrorBox({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="errbox" role="alert">
      <b>{title}</b>
      {body ? <span>{body}</span> : null}
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="switch" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} />
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  radio,
}: {
  value: T;
  options: ReadonlyArray<readonly [T, string, string?]>;
  onChange: (next: T) => void;
  label: string;
  radio?: boolean;
}) {
  return (
    <div className="seg" role={radio ? 'radiogroup' : 'group'} aria-label={label}>
      {options.map(([key, text, icon]) => (
        <button
          key={key}
          type="button"
          role={radio ? 'radio' : undefined}
          aria-checked={radio ? value === key : undefined}
          aria-pressed={radio ? undefined : value === key}
          onClick={() => onChange(key)}
        >
          {icon ? <Icon name={icon} className="xs" /> : null}
          {text}
        </button>
      ))}
    </div>
  );
}

export function SelectField({
  name,
  label,
  options,
  value,
  onChange,
  small = true,
}: {
  name: string;
  label: string;
  options: ReadonlyArray<readonly [string, string]>;
  value: string;
  onChange: (next: string) => void;
  small?: boolean;
}) {
  return (
    <span className="select-wrap">
      <select className={`select${small ? ' sm' : ''}`} name={name} aria-label={label} value={value} onChange={(event) => onChange(event.currentTarget.value)}>
        {options.map(([key, text]) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </select>
      <Icon name="chevDown" />
    </span>
  );
}

export function SearchField({ value, onChange, placeholder, label }: { value: string; onChange: (next: string) => void; placeholder: string; label: string }) {
  return (
    <div className="input-wrap">
      <Icon name="search" className="sm" />
      <input className="input sm" type="search" value={value} placeholder={placeholder} aria-label={label} onChange={(event) => onChange(event.currentTarget.value)} />
    </div>
  );
}

export function FilterChip({ label, count, pressed, onClick }: { label: string; count?: number; pressed: boolean; onClick: () => void }) {
  return (
    <button type="button" className="chip-btn" aria-pressed={pressed} onClick={onClick}>
      {label}
      {count != null ? <span className="n">{count}</span> : null}
    </button>
  );
}

/**
 * A horizontal chip row that keeps the pressed chip visible.
 *
 * The row is an `overflow-x: auto` scroller (`.chips.scroll`) holding more chips than fit — the VAT
 * period picker carries 13 bi-monthly periods, ~770px of them. At 390px the *selected* period would
 * otherwise sit outside the scroller's start edge with `scrollLeft` at 0, so a phone opened on
 * מע"מ showed the active period clipped in half. Scrolling it into view on mount and on every
 * change is the fix; `behavior: 'auto'` because this is a correction of the initial paint, not an
 * animation, and mockup-rules.md requires `prefers-reduced-motion` to be respected.
 */
export function ChipScroller({ children, label }: { children: ReactNode; label?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const box = ref.current;
    const selected = box?.querySelector('[aria-pressed="true"]');
    if (!box || !(selected instanceof HTMLElement)) return;
    // Only the scroller's own `scrollLeft` moves. `scrollIntoView()` would walk up the ancestor
    // chain and could scroll the page itself; centring by the measured delta cannot. The arithmetic
    // is relative, so it is correct under RTL too (where `scrollLeft` is negative in this engine).
    const outer = box.getBoundingClientRect();
    const inner = selected.getBoundingClientRect();
    const delta = inner.left + inner.width / 2 - (outer.left + outer.width / 2);
    if (Math.abs(delta) > 1) box.scrollLeft += delta;
  });
  return (
    <div className="chips scroll" ref={ref} {...(label === undefined ? {} : { role: 'group', 'aria-label': label })}>
      {children}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={`sk${className ? ` ${className}` : ''}`} />;
}

/** The first-load skeleton of a 12-column screen (spec §4: skeleton on the FIRST load only). */
export function ScreenSkeleton() {
  const card = (height: number, span: number, key: number) => (
    <div className={`card col-${span}`} key={key} aria-hidden="true" style={{ minHeight: `${height}px` }}>
      <Skeleton className="t" />
      <Skeleton className="v" />
      <Skeleton className="l" />
      <div className="sk ch" style={{ height: `${height - 110}px` }} />
    </div>
  );
  return (
    <div className="grid" role="status" aria-label={he.app.loading}>
      <span className="sr">{he.app.loading}</span>
      {card(260, 5, 1)}
      {card(260, 4, 2)}
      {card(260, 3, 3)}
      {card(300, 4, 4)}
      {card(300, 4, 5)}
      {card(300, 4, 6)}
    </div>
  );
}

/** A sortable table header cell (spec §8: a real button inside the `<th>`, `aria-sort` on the th). */
export function SortableTh<T extends string>({
  label,
  col,
  sort,
  onSort,
  numeric,
}: {
  label: string;
  col: T;
  sort: { col: T; dir: 'asc' | 'desc' };
  onSort: (col: T) => void;
  numeric?: boolean;
}) {
  const active = sort.col === col;
  return (
    <th scope="col" className={`sortable${numeric ? ' n' : ''}`} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="th-btn" aria-label={`מיין לפי ${label}`} onClick={() => onSort(col)}>
        {label}
        <Icon name="sort" className="sort-ic" />
      </button>
    </th>
  );
}

/** Generic sort helper shared by the tables. */
export function toggleSort<T extends string>(sort: { col: T; dir: 'asc' | 'desc' }, col: T, defaultDesc: readonly T[]): { col: T; dir: 'asc' | 'desc' } {
  if (sort.col === col) return { col, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return { col, dir: defaultDesc.includes(col) ? 'desc' : 'asc' };
}
